/**
 * Type surface for the WebMCP browser API, which lets a page hand AI agents a set of
 * callable tools instead of making them drive the DOM.
 *
 * The API is an origin trial (Chrome 149+) with no TypeScript lib types yet, so the
 * shapes below are hand-written from the explainer and the Chrome documentation:
 * https://developer.chrome.com/docs/ai/webmcp/imperative-api
 *
 * `document.modelContext` is the current spelling. `navigator.modelContext` was the
 * original one and is deprecated as of Chrome 150 — do not add a fallback to it.
 */

export type WebMcpToolResult = {
  content: Array<{ type: 'text'; text: string }>;
};

/** JSON Schema subset the API accepts for tool inputs. */
export type WebMcpInputSchema = {
  type: 'object';
  properties: Record<string, unknown>;
  required?: Array<string>;
};

export type WebMcpTool = {
  /** Snake-case, action-oriented, unique per document. */
  name: string;
  description: string;
  inputSchema: WebMcpInputSchema;
  /** Resolves `null` when the tool navigates instead of returning content. */
  execute: (args: Record<string, unknown>, context?: { signal?: AbortSignal }) => Promise<WebMcpToolResult | null>;
};

export type WebMcpRegisterOptions = {
  /** Aborting unregisters the tool without disturbing in-flight executions. */
  signal?: AbortSignal;
  exposedTo?: Array<string>;
};

export type ModelContext = {
  // Returns undefined in Chrome's stub state (origin trial active, no agent connected).
  registerTool: (tool: WebMcpTool, options?: WebMcpRegisterOptions) => Promise<void> | void;
};

declare global {
  interface Document {
    modelContext?: ModelContext;
  }
}

/** Feature detection. Returns null during SSR and in browsers without the origin trial. */
export function getModelContext(): ModelContext | null {
  if (typeof document === 'undefined') return null;
  return document.modelContext ?? null;
}

export function textResult(text: string): WebMcpToolResult {
  return { content: [{ type: 'text', text }] };
}

/** Shared so every tool words the unresolved-identity wait the same way. */
export function identityLoadingResult(): WebMcpToolResult {
  return textResult('The sign-in state is still loading. Call this tool again in a moment.');
}
